<template>
  <div>
    <data-table
      ref="datatableRef"
      :config="datatableConfig"
      :data="templates"
      :autoLoad="false"
      :refreshButton="refreshButtonConfig"
      @refresh="loadData"
      @edit-click="handleEditClick"
      @button-click="handleButtonClick">
    </data-table>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { log } from '../../utils/DebugLogger';
import { useSiteTemplates } from '../../composables/useSiteTemplates';
import DataTable from '../../grid/DataTable.vue';
import type { SiteTemplates_TemplateRow } from '../../contracts/SiteTemplates_TemplateRow';

type TemplateId = number | string;

interface DataTableInstance {
  loadData: () => void;
}

interface CellParams {
  value: unknown;
  data?: SiteTemplates_TemplateRow;
}

interface ButtonClickData {
  action: string;
  data: SiteTemplates_TemplateRow;
}

const emit = defineEmits<{
  edit: [templateId: TemplateId];
}>();

const { t } = useI18n();
const { fetchTemplates } = useSiteTemplates();

const datatableRef = ref<DataTableInstance | null>(null);
const templates = ref<SiteTemplates_TemplateRow[]>([]);

/** cellRenderer output is v-html'd by DataTable — stored text must be escaped. */
const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const tableColumns = computed(() => {
  return [
    {
      data: 'id',
      title: t('ID'),
      width: 90,
      sortable: true,
      filterType: 'text',
      renderType: 'clickable_id'
    },
    {
      data: 'formulario',
      title: t('Formulário'),
      width: 160,
      sortable: true,
      filterType: 'text'
    },
    {
      data: 'assunto',
      title: t('Assunto do e-mail'),
      width: 280,
      sortable: true,
      filterType: 'text'
    },
    {
      data: 'destinatarios',
      title: t('Destinatários'),
      width: 280,
      sortable: false,
      filterType: 'text',
      cellRenderer: (params: CellParams): string => {
        const lista = params.value as string | undefined;
        return escapeHtml((lista ?? '').split(',').map((e) => e.trim()).filter((e) => e !== '').join(', '));
      }
    },
    {
      data: 'ativo',
      title: t('Ativo'),
      width: 100,
      sortable: true,
      cellRenderer: (params: CellParams): string => {
        return params.value ? t('Sim') : t('Não');
      }
    },
    {
      data: 'id',
      title: t('Editar'),
      orderable: false,
      renderType: 'clickable_button',
      buttonText: t('Editar'),
      buttonIcon: 'pi pi-pencil',
      buttonClass: 'p-button-sm p-button-outlined',
      buttonAction: 'edit'
    }
  ];
});

const datatableConfig = computed(() => {
  return {
    filename: `templates_site_${new Date().toISOString().split('T')[0]}`,
    columns: tableColumns.value,
    rowTestId: (data: Record<string, unknown>) => {
      const id = data?.id;
      return id == null ? '' : `site-templates-row-${id}`;
    },
    pageLength: 25,
    pagination: true,
    rowSelection: 'single',
    sortable: true,
    resizableColumns: true,
    showFilters: true,
    filterDisplay: 'row',
    stripedRows: true,
    showGridlines: true,
    size: 'small'
  };
});

const refreshButtonConfig = computed(() => {
  return {
    show: true,
    text: t('Atualizar')
  };
});

const loadData = async (): Promise<void> => {
  log('SiteTemplatesTable', 'loadData', 'Loading templates');
  try {
    const items = await fetchTemplates();
    templates.value = items || [];
    log('SiteTemplatesTable', 'loadData', 'Loaded templates', templates.value.length);
  } catch (err) {
    const error = err as Error;
    log('SiteTemplatesTable', 'loadData', 'Error loading templates', error);
    eventBus.emit('globalMessage.error', t('Erro ao carregar templates') + ': ' + error.message);
  }
};

const handleEditClick = (templateId: TemplateId): void => {
  log('SiteTemplatesTable', 'handleEditClick', 'Edit clicked for ID', templateId);
  emit('edit', templateId);
};

const handleButtonClick = (buttonData: ButtonClickData): void => {
  log('SiteTemplatesTable', 'handleButtonClick', 'Button clicked', buttonData);
  const id = buttonData.data?.id;
  if (buttonData.action === 'edit' && id !== undefined) {
    emit('edit', id);
  }
};

const onSaved = (): void => {
  void loadData();
};

onMounted(() => {
  log('SiteTemplatesTable', 'mounted', 'Setting up event listeners');
  eventBus.on('site-templates.saved', onSaved);
  void loadData();
});

onBeforeUnmount(() => {
  log('SiteTemplatesTable', 'beforeUnmount', 'Cleaning up event listeners');
  eventBus.off('site-templates.saved', onSaved);
});

defineExpose({
  loadData
});
</script>

<style scoped>
</style>
